import type { ObjectActionInfo } from '../../engine/worker/protocol.ts';
import type { Editing, Selection } from '../model.ts';
import { CopyIcon, DeleteIcon, EditIcon, MoveIcon } from './icons.tsx';

/** Actions for the selection: edit a line's text, delete; it is moved by dragging and turned by its grip (PageView). */
export function SelectionToolbar(props: { index: number; selection: Selection; at: [number, number]; pageWidth: number; disabled: boolean; readOnly: boolean; setEditing: (e?: Editing) => void; setSelection: (s?: Selection) => void; onObject: (sel: Selection, action: ObjectActionInfo) => void; onCopy: (sel: Selection) => void; onReplace: (sel: Selection, file: File) => void; startDrag: (e: React.PointerEvent) => void }) {
  const sel = props.selection;
  const lone = sel.items.length === 1 ? sel.items[0] : undefined;
  // A line can be edited if any piece can (the engine refuses a change to a piece that cannot); deleted or moved only if every piece can.
  const editLocked = lone?.kind === 'span' ? (lone.pieces.every((x) => x.editLocked) ? lone.pieces[0].editLocked : undefined) : 'images have no text to edit';
  const locked = sel.items.flatMap((it) => (it.kind === 'span' ? it.pieces : [])).find((x) => x.locked)?.locked;
  return (
    // A small floating bar next to the selection, kept inside the page; PageView chooses above or below (AC-UX-4).
    <div data-testid="toolbar" role="toolbar" aria-label="Selection" className="selection-toolbar" style={{ left: props.at[0], top: props.at[1] }}>
      {lone?.kind === 'span' && !props.readOnly && (
        <button
          data-testid="edit-text"
          disabled={props.disabled || !!editLocked}
          title={editLocked ? `The text cannot be edited: ${editLocked}` : 'Edit the text (or double-click the line)'}
          onClick={() => {
            props.setSelection(undefined);
            props.setEditing({ page: props.index, line: lone.line, span: lone.pieces[0], text: lone.line.text });
          }}
        >
          <EditIcon size={16} />
          <span>Edit</span>
        </button>
      )}
      {lone?.kind === 'image' && !props.readOnly && (
        // Replace by a PNG or JPEG file (AC-IMG-2): fitted inside the image's place.
        <label data-testid="replace-image" className={`btn${props.disabled ? ' is-disabled' : ''}`} title="Replace by a PNG or JPEG file">
          <input data-testid="replace-image-file" type="file" accept="image/png,image/jpeg" hidden disabled={props.disabled} onChange={(e) => e.target.files?.[0] && props.onReplace(sel, e.target.files[0])} />
          <span>Replace</span>
        </label>
      )}
      <button data-testid="copy" title="Copy to the clipboard (Ctrl+C)" onClick={() => props.onCopy(sel)}>
        <CopyIcon size={16} />
        <span>Copy</span>
      </button>
      {/* View mode (D-74): Copy only. */}
      {!props.readOnly && (
        <button data-testid="delete" className="danger-quiet" disabled={props.disabled || !!locked} title={locked ? `Part of this line cannot be deleted: ${locked}` : 'Delete (Delete key)'} onClick={() => props.onObject(sel, { kind: 'delete' })}>
          <DeleteIcon size={16} />
          <span>{lone ? 'Delete' : `Delete ${sel.items.length}`}</span>
        </button>
      )}
      {/* A handle to grab: dragging it moves the selection like dragging the selection itself. */}
      {!props.readOnly && (
        <button type="button" data-testid="move-handle" className="move-handle" aria-label="Move (drag)" title="Drag to move the selection" disabled={props.disabled || !!locked} onPointerDown={props.startDrag}>
          <MoveIcon size={16} />
        </button>
      )}
    </div>
  );
}
